import type { ProjectFileUsageType } from '@autoriz/shared/files'

/**
 * Catching a mandatory piece dropped into "Autres documents" (#668).
 *
 * "Autres documents" is optional, so a procuration filed there leaves the
 * dossier's CLIENT_PROXY slot still counted as missing — the commercial has
 * uploaded the right file and the page still refuses to send. Nothing warned
 * them, and nothing in the file's destination said the two were different.
 *
 * The matcher is deliberately conservative. It reads the filename against the
 * words of the slots that are STILL OWED, never against ones already satisfied,
 * and it only ever produces a warning the commercial can ignore — a file is
 * never moved or refused on the strength of its name.
 */

/** A still-owed mandatory slot, as this matcher needs to see it. */
export type OwedSlot = {
  usage: ProjectFileUsageType
  title: string
}

// Diacritic-insensitive, punctuation-free words of 4+ letters. Shorter tokens
// ("de", "du", "vue") match far too much to be evidence of anything.
const MIN_TOKEN_LENGTH = 4

const tokenize = (text: string): ReadonlyArray<string> =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= MIN_TOKEN_LENGTH)

// Words that appear in a slot title without identifying it — every photo slot
// contains "photos", so a file called "photos.jpg" is evidence of nothing.
const AMBIGUOUS = new Set(['photo', 'photos', 'document', 'documents', 'projet'])

/**
 * The slot a file dropped into "Autres documents" probably belongs to, or
 * `null` when the filename says nothing useful. A tie between two slots is
 * `null` too: naming the wrong section is worse than naming none.
 */
export const misplacedDocumentSlot = (
  filename: string,
  owed: ReadonlyArray<OwedSlot>,
): OwedSlot | null => {
  const fileTokens = new Set(tokenize(filename))
  if (fileTokens.size === 0) return null

  const matches = owed.filter((slot) =>
    tokenize(slot.title).some(
      (word) => !AMBIGUOUS.has(word) && fileTokens.has(word),
    ),
  )
  return matches.length === 1 ? matches[0] : null
}
